import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { Button, Dialog } from "@chotca/ui";
import { useSyncPlanPurchase } from "@chotca/api-client";
import type { PlanPurchaseQrResponse } from "@chotca/config";

function fmtVnd(n: number): string {
  return `${n.toLocaleString("vi-VN")}₫`;
}

// The VietQR bank-transfer QR shown while an order is PENDING. On "Đã chuyển
// khoản" it nudges Casso to sync, then navigates to the invoice/result screen
// (/billing/:orderCode) instead of silently closing — so the buyer always lands
// on a page that confirms the order and self-updates PENDING → PAID.
//
// Reused by the upgrade/top-up modals and by the invoice screen's "reopen QR"
// action for a PENDING order.
export function VietQrDialog({
  qr,
  open,
  orgId,
  onClose,
}: {
  qr: PlanPurchaseQrResponse | null;
  open: boolean;
  orgId: string;
  onClose: () => void;
}) {
  const t = useTranslations("billing");
  const nav = useNavigate();
  const sync = useSyncPlanPurchase(orgId);

  async function handlePaid() {
    if (!qr) {
      onClose();
      return;
    }
    try {
      await sync.mutateAsync(qr.orderCode);
    } catch {
      // ignore — the webhook still settles the order regardless.
    }
    onClose();
    nav(`/billing/${encodeURIComponent(qr.orderCode)}`);
  }

  return (
    <Dialog open={open} onClose={onClose} title={t("qrTitle")}>
      {qr ? (
        <div className="space-y-4 text-center">
          {qr.qrDataUrl ? (
            <img
              src={qr.qrDataUrl}
              alt="VietQR"
              className="mx-auto h-64 w-64 rounded-xl border border-mist bg-paper object-contain p-2"
            />
          ) : (
            <p className="break-all text-body-xs text-ash">{qr.qrCode}</p>
          )}
          <div className="rounded-xl bg-mist p-3 text-left text-body-sm text-ink">
            <Row label={t("detailAmount")} value={fmtVnd(qr.amountVnd)} />
            <Row label={t("detailAccountName")} value={qr.accountName} />
            <Row label={t("detailAccountNo")} value={qr.accountNo} />
            <Row label={t("detailAddInfo")} value={qr.addInfo} />
          </div>
          <p className="text-body-sm text-ash">{t("qrHint")}</p>
          <Button
            className="w-full"
            loading={sync.isPending}
            onClick={handlePaid}
          >
            {t("qrPaidCta")}
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-ash">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
